import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { format, isSameDay, isValid, parseISO, startOfWeek } from "date-fns";
import { it } from "date-fns/locale";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import { avvisoLettura } from "../../utils/lettureOdoo";
import * as scheduleApi from "../../api/schedule";
import { InstallationSchedule, TipoDispositivo } from "../../types";
import { coloreStato, STATO_ORDER } from "../../utils/statoColors";
import { Badge } from "../../components/ui/Badge";
import { RitardoBanner } from "../../components/ui/RitardoBanner";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { CampiOrario } from "../../components/ui/CampiOrario";
import { Modal } from "../../components/ui/Modal";
import { SlideOver } from "../../components/ui/SlideOver";
import {
  RiferimentiOdoo,
  ValoriRiferimentiOdoo,
  RIFERIMENTI_ODOO_VUOTI,
  riferimentiOdooDaRecord,
  payloadRiferimentiOdoo,
  senzaRiferimentiOdoo,
  usaPuoGestireRiferimentiOdoo,
} from "../../components/ui/RiferimentiOdoo";
import { MediaUploader } from "../../components/ui/MediaUploader";
import { ExportButton } from "../../components/ui/ExportButton";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";
import { CalendarBand, CalendarView, DROP_GIORNO_PREFIX } from "../../components/ui/CalendarBand";
import { DaPianificare } from "../../components/ui/DaPianificare";
import { annunciTrascinamento, istruzioniTrascinamento } from "../../utils/dndAnnunci";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { DeviceFisicoAutocomplete } from "../../components/ui/DeviceFisicoAutocomplete";
import { CensimentoDevice } from "../../components/ui/CensimentoDevice";
import { SopralluoghiCollegati } from "../../components/ui/SopralluoghiCollegati";
import { TipoPossessoDevice } from "../../api/devices";
import { DispositivoAutocomplete } from "../../components/ui/DispositivoAutocomplete";
import { modelloDaDevice } from "../../utils/modelloDaDevice";
import { DispositivoMateriale } from "../../components/ui/DispositivoMateriale";
import type { ClienteSuggestion } from "../../api/schedule";
import * as anagraficaApi from "../../api/anagrafica";
import { CentroAssistenzaOption } from "../../api/anagrafica";
import { etichettaIpotesiData, isMesePlaceholder } from "../../utils/dataIpotesi";
import { AvvisoBozza, StatoBozzaNelForm, confermaDopoIlSalvataggio, leggiBozzaPerForm } from "../StatoLavori/BozzaNelForm";
import { bozzaDalLink } from "../StatoLavori/presentazione";
import { intervalloOrario, orarioPerInput } from "../../utils/orario";
import { AssegnatarioSelect, useCentroDelChiamante } from "../../components/ui/AssegnatarioSelect";
import { ListaVuotaOperatore, useIsOperatore, useNomeOrganizzazione } from "../../components/ui/ListaVuota";
import { usePannelloRecord, useRicercaUrl, useScriviParametri } from "../../hooks/useStatoUrl";

const ZONE = ["Nord Ovest", "Nord Est", "Centro", "Sud", "Isole"];
const TIPI_DISPOSITIVO: TipoDispositivo[] = ["Cassaforte", "Rendiresto", "TVCC", "Allarme", "Altro"];

type FormValues = {
  insegna: string;
  indirizzo: string;
  localita: string;
  provincia: string;
  zona: string;
  tipo_dispositivo: string;
  macchina: string;
  ipotesi_data: string;
  orario_inizio: string;
  orario_fine: string;
  data_effettiva: string;
  stato: string;
  note: string;
  richiede_attivazione: boolean;
  assegnato_utente_id: string; // persona incaricata; "" = nessuna
  installatore_id: string; // Centro Assistenza assegnato (FK); "" = nessuno
  // Collegamento all'anagrafica cliente (Punto B, solo admin/staff).
  cliente_id: string; // FK cliente; "" = non collegata
  cliente_nome: string; // display del cliente scelto (non inviato come tale)
  // Campi assorbiti dal Modulo ritirato (unificazione installazioni).
  tecnico: string;
  sede_id: string; // sede reale collegata; "" = nessuna
  dispositivo_marca: string;
  dispositivo_modello: string;
  dispositivo_matricola: string;
  dispositivo_catalogo_id: string; // link al Catalogo; "" = nessuno
  /**
   * Gli id dei sopralluoghi collegati. Erano uno solo fino al 30/08/2026.
   *
   * Il backend riscrive l'insieme **solo se il campo arriva**: mandarlo vuoto
   * scollega tutto, non mandarlo lascia com'e'. Qui viaggia sempre, perche' il
   * pannello e' l'unico posto da cui si cambia.
   */
  sopralluoghi: string[];
  // I cinque riferimenti OdA/OdV dentro il form, così `reset()` li riallinea
  // aprendo un altro record — al contrario dello stato del censimento, che vive
  // fuori e va azzerato a mano (vedi `azzeraCensimento`).
} & ValoriRiferimentiOdoo;

function toDateInputValue(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function emptyFormValues(prefilledDate?: string): FormValues {
  return {
    insegna: "",
    indirizzo: "",
    localita: "",
    provincia: "",
    zona: "",
    tipo_dispositivo: "",
    macchina: "",
    ipotesi_data: prefilledDate ?? "",
    orario_inizio: "",
    orario_fine: "",
    data_effettiva: "",
    stato: "Da programmare",
    note: "",
    richiede_attivazione: false,
    assegnato_utente_id: "",
    installatore_id: "",
    cliente_id: "",
    cliente_nome: "",
    tecnico: "",
    sede_id: "",
    dispositivo_marca: "",
    dispositivo_modello: "",
    dispositivo_matricola: "",
    dispositivo_catalogo_id: "",
    sopralluoghi: [],
    ...RIFERIMENTI_ODOO_VUOTI,
  };
}

function recordToFormValues(record: InstallationSchedule): FormValues {
  return {
    insegna: record.insegna ?? "",
    indirizzo: record.indirizzo ?? "",
    localita: record.localita ?? "",
    provincia: record.provincia ?? "",
    zona: record.zona ?? "",
    tipo_dispositivo: record.tipo_dispositivo ?? "",
    macchina: record.macchina ?? "",
    ipotesi_data: toDateInputValue(record.ipotesi_data),
    // Non `toDateInputValue`: un orario non e' una data e affettarlo ai primi
    // dieci caratteri darebbe "1970-01-01". Vedi utils/orario.ts.
    orario_inizio: orarioPerInput(record.orario_inizio),
    orario_fine: orarioPerInput(record.orario_fine),
    data_effettiva: toDateInputValue(record.data_effettiva),
    stato: record.stato,
    note: record.note ?? "",
    richiede_attivazione: record.richiede_attivazione ?? false,
    assegnato_utente_id: record.assegnato_utente_id ?? "",
    installatore_id: record.installatore_id ?? "",
    cliente_id: record.cliente_id ?? "",
    cliente_nome: record.cliente_nome ?? "",
    tecnico: record.tecnico ?? "",
    sede_id: record.sede_id ?? "",
    dispositivo_marca: record.dispositivo_marca ?? "",
    dispositivo_modello: record.dispositivo_modello ?? "",
    dispositivo_matricola: record.dispositivo_matricola ?? "",
    dispositivo_catalogo_id: record.dispositivo_catalogo_id ?? "",
    sopralluoghi: record.sopralluoghi.map((r) => r.sopralluogo.id),
    ...riferimentiOdooDaRecord(record),
  };
}

/** La vista del calendario nell'URL (`?vista=mese&dal=2026-10-01`), in italiano come gli altri parametri. */
const VISTA_IN_URL: Record<CalendarView, string> = { week: "settimana", month: "mese", year: "anno" };
const VISTA_DA_URL: Record<string, CalendarView> = { settimana: "week", mese: "month", anno: "year" };

export default function Schedule() {
  const { user, hasPermission } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const ritardoFilter = searchParams.get("ritardo") === "true";
  const isOperatore = useIsOperatore();
  const organizzazione = useNomeOrganizzazione();
  // Filtro "aspettano l'Attivazione": i due possono coesistere, e insieme
  // isolano i record da guardare per primi — in ritardo E senza attivazione.
  const attivazioneFilter = searchParams.get("attivazione_mancante") === "true";

  // Drill-down dai grafici delle Statistiche (Fase 1.2): un click su una barra/
  // fetta apre il Calendario filtrato per quella dimensione. Le Statistiche
  // aggregano proprio installation_schedule, quindi i conteggi combaciano.
  // Una sola dimensione attiva per volta (dal click). Mappa param → campo record.
  const FILTER_FIELDS: Record<string, { field: keyof InstallationSchedule; label: string }> = {
    stato: { field: "stato", label: "stato" },
    zona: { field: "zona", label: "zona" },
    tipo: { field: "tipo_dispositivo", label: "tipo dispositivo" },
    installatore: { field: "installatore", label: "centro assistenza" },
  };
  const activeFilterKey = (["stato", "zona", "tipo", "installatore"] as const).find((k) => searchParams.get(k));
  const activeFilter = activeFilterKey
    ? { ...FILTER_FIELDS[activeFilterKey], value: searchParams.get(activeFilterKey) as string }
    : null;

  const [records, setRecords] = useState<InstallationSchedule[]>([]);
  const filteredRecords = activeFilter
    ? records.filter((r) => String(r[activeFilter.field] ?? "") === activeFilter.value)
    : [];
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncingPull, setIsSyncingPull] = useState(false);
  const [isSyncingPush, setIsSyncingPush] = useState(false);

  // Riassegnazione del Centro Assistenza: consentita solo ad admin/staff, come
  // lato backend. Un CA vede il centro assegnato in sola lettura.
  const canReassign = user?.role === "admin" || user?.role === "staff";
  // Stesso predicato di `canReassign`, ma un'altra domanda: quello dice chi può
  // spostare un'installazione fra i centri, questo chi può vedere i riferimenti
  // OdA/OdV. Tenerli separati costa una riga e serve il giorno in cui uno dei due
  // cambia — vedi `usaPuoGestireRiferimentiOdoo`.
  const puoGestireOrdini = usaPuoGestireRiferimentiOdoo();
  // Un CA non sceglie il centro: in creazione `installatore_id` resta vuoto e
  // lo imposta il backend. Per l'elenco "Assegnato a" vale già il suo.
  const centroDelChiamante = useCentroDelChiamante();
  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);

  useEffect(() => {
    if (!canReassign) return;
    anagraficaApi.listCentriAssistenza().then(({ centri }) => setCentri(centri)).catch(() => setCentri([]));
  }, [canReassign]);

  const [editingRecord, setEditingRecord] = useState<InstallationSchedule | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // Punto B: suggerimento cliente per schede scollegate (solo admin/staff).
  const [clienteSuggestion, setClienteSuggestion] = useState<ClienteSuggestion | null>(null);
  // La bozza di Stato lavori da cui si apre «Nuova installazione» (`&bozza=<id>`, PR C2).
  const [bozzaNelForm, setBozzaNelForm] = useState<StatoBozzaNelForm | null>(null);
  // Finché la bozza si legge il form resta fermo: il precompilato lo sostituisce tutto.
  const bozzaInCaricamento = bozzaNelForm?.stato === "caricamento";
  const navigate = useNavigate();

  // Ricerca, installazione aperta e periodo del calendario stanno nell'URL
  // come i filtri che arrivano dalla Dashboard (vedi `hooks/useStatoUrl.ts`):
  // tornando indietro, o aprendo un link, si ritrova la stessa vista.
  const scrivi = useScriviParametri();
  const { testo: ricerca, setTesto: setRicerca, valore: ricercaAttiva } = useRicercaUrl();

  // Vista e primo giorno del periodo mostrato. Letti all'apertura della
  // pagina; da lì in poi li tiene il calendario, che li riscrive qui a ogni
  // cambio. La settimana corrente non si scrive: è dove il calendario si apre
  // comunque, e l'URL della pagina appena aperta resta pulito.
  const vistaIniziale = VISTA_DA_URL[searchParams.get("vista") ?? ""];
  const dalUrl = searchParams.get("dal");
  const dataIniziale = dalUrl && isValid(parseISO(dalUrl)) ? parseISO(dalUrl) : undefined;
  function cambioPeriodo(da: Date, _a: Date, vista: CalendarView) {
    const predefinito = vista === "week" && isSameDay(da, startOfWeek(new Date(), { weekStartsOn: 1 }));
    scrivi(predefinito ? { vista: null, dal: null } : { vista: VISTA_IN_URL[vista], dal: format(da, "yyyy-MM-dd") });
  }

  async function loadRecords() {
    setIsLoading(true);
    try {
      // Nessun filtro data: carichiamo tutto lo scope dell'utente e navighiamo
      // la settimana lato client, così "Da pianificare" (senza data) resta
      // sempre visibile indipendentemente dalla settimana corrente.
      const { records: list } = await scheduleApi.listSchedule({
        ...(ritardoFilter ? { ritardo: true } : {}),
        ...(attivazioneFilter ? { attivazione_mancante: true } : {}),
        ...(ricercaAttiva ? { search: ricercaAttiva } : {}),
      });
      setRecords(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il calendario."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ritardoFilter, attivazioneFilter, ricercaAttiva]);

  /**
   * La pila laterale: le installazioni che una data vera non ce l'hanno ancora.
   *
   * Sono DUE casi, non uno. Quelle senza `ipotesi_data` (mai stata indicata) e
   * quelle il cui `ipotesi_data` cade sul giorno 1, che non è una data ma il
   * segnaposto di un mese — chi pianifica sa che si va a settembre e scrive
   * 01/09 perché la colonna vuole un giorno (vedi `utils/dataIpotesi.ts`).
   *
   * Prima le seconde finivano disegnate nella fascia, su un 1° settembre, dove
   * una tessera in una cella-giorno dice "appuntamento preso in quel giorno" —
   * cioè esattamente la cosa che non è vera, proprio nella vista che serve a
   * pianificare. Un contrassegno sulla tessera avrebbe attenuato l'equivoco
   * senza toglierlo: il posto in cui una cosa è disegnata è un'affermazione più
   * forte di qualunque etichetta le si metta accanto.
   *
   * Qui invece stanno con le altre da pianificare, e la tessera dichiara il
   * mese. Trascinarla su un giorno resta il gesto che le dà la data vera.
   */
  const daPianificare = useMemo(
    () => records.filter((r) => !r.ipotesi_data || isMesePlaceholder(r.ipotesi_data, r.stato)),
    [records]
  );

  // --- Trascinamento di una tessera su un giorno del calendario -------------
  // Il rilascio assegna la data e porta lo stato a "Programmata": è la
  // transizione che l'operatore farebbe a mano aprendo il pannello, qui in un
  // gesto solo. Se il record era già in uno stato più avanzato lo stato resta
  // com'è — trascinare per correggere una data non deve far tornare indietro
  // un'installazione già effettuata.
  const [recordTrascinato, setRecordTrascinato] = useState<InstallationSchedule | null>(null);
  // Il sensore parte dopo 6px di movimento, così il click sulla maniglia resta
  // un click e non un trascinamento involontario.
  // Puntatore/touch con soglia (il click sulla maniglia resta un click) più
  // tastiera: la maniglia è già focusabile, con Spazio avvia lo spostamento e
  // le frecce scelgono il giorno.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  // La strategia predefinita di dnd-kit confronta il RETTANGOLO dell'elemento
  // trascinato con quello dei bersagli: l'anteprima è larga quanto una tessera
  // e si sovrappone a più celle, quindi rilasciando su mercoledì il record
  // poteva finire al giovedì. Qui vince il giorno sotto la punta del
  // puntatore, che è ciò che l'utente vede e si aspetta; il confronto fra
  // rettangoli resta come riserva per i casi senza puntatore.
  const collisionDetection: CollisionDetection = (args) => {
    const sottoIlPuntatore = pointerWithin(args);
    return sottoIlPuntatore.length > 0 ? sottoIlPuntatore : rectIntersection(args);
  };

  function handleDragStart(e: DragStartEvent) {
    const rec = (e.active.data.current as { record?: InstallationSchedule } | undefined)?.record;
    setRecordTrascinato(rec ?? null);
  }

  async function handleDragEnd(e: DragEndEvent) {
    const record = recordTrascinato;
    setRecordTrascinato(null);
    if (!record || !e.over) return;

    const overId = String(e.over.id);
    if (!overId.startsWith(DROP_GIORNO_PREFIX)) return;
    const isoDate = overId.slice(DROP_GIORNO_PREFIX.length);

    const nuovoStato = record.stato === "Da programmare" ? "Programmata" : record.stato;
    try {
      await scheduleApi.updateSchedule(record.id, { ipotesi_data: isoDate, stato: nuovoStato });
      showToast(
        `${record.insegna ?? "Installazione"} programmata per il ${format(new Date(isoDate), "d MMMM yyyy", { locale: it })}.`,
        "success"
      );
      loadRecords();
    } catch (err) {
      // Il backend resta l'autorità sui permessi: `can_edit` evita di offrire
      // il trascinamento dove non serve, ma se un 403 arriva comunque (record
      // riassegnato nel frattempo) la lista viene ricaricata e nulla resta
      // spostato solo a schermo.
      showToast(getErrorMessage(err, "Impossibile programmare l'installazione."), "error");
      loadRecords();
    }
  }

  // Device fisico scelto dall'autocomplete. Non e' un campo del form perche'
  // non si salva come tale: la rotta ricava `device_id` dalla matricola e
  // rifiuta una chiave presa dal body (vedi il commento nella POST). Serve a
  // sapere se il pezzo e' gia' identificato, e quindi se proporre il
  // censimento.
  const [deviceSceltoId, setDeviceSceltoId] = useState("");
  // Spunta "censisci il dispositivo" e possesso dichiarato accanto ad essa.
  // Vedi `components/ui/CensimentoDevice.tsx` per il perche' non sia un
  // automatismo.
  const [censisciDevice, setCensisciDevice] = useState(false);
  const [possessoCensimento, setPossessoCensimento] = useState<TipoPossessoDevice | "">("");

  const formMethods = useForm<FormValues>({ defaultValues: emptyFormValues() });
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = formMethods;

  // La riga aperta nel pannello è ancora un segnaposto di mese? Decide se le
  // caselle dell'orario sono utilizzabili — stessa regola, stessa funzione
  // dell'elenco e della fascia, così non può divergere in un punto solo.
  const segnapostoMese = isMesePlaceholder(watch("ipotesi_data"), watch("stato"));

  /**
   * Prepara il modulo di creazione; il pannello lo apre `?new=1`, con il giorno
   * cliccato nel calendario in `&data=`.
   *
   * Handoff da un Sopralluogo: `&sopralluogo=<id>&cliente_nome=<nome>` apre
   * "Nuova installazione" precompilata e collegata al sopralluogo.
   *
   * Da una bozza di Stato lavori: `&bozza=<id>&pratica=<id>` (PR C2). I campi
   * vengono dalla bozza (sede, cliente, device, sopralluogo), e dopo il
   * salvataggio la bozza risulta confermata e si torna alla pratica.
   */
  function openCreate() {
    const qui = new URLSearchParams(window.location.search);
    const sopralluogoId = qui.get("sopralluogo");
    setEditingRecord(null);
    setClienteSuggestion(null);
    reset(
      sopralluogoId
        ? { ...emptyFormValues(), insegna: qui.get("cliente_nome") ?? "", sopralluoghi: [sopralluogoId] }
        : emptyFormValues(qui.get("data") ?? undefined)
    );
    azzeraCensimento();
    const daBozza = bozzaDalLink(window.location.search);
    setBozzaNelForm(daBozza ? { stato: "caricamento", ...daBozza } : null);
    if (daBozza) precompilaDaBozza(daBozza.bozzaId, daBozza.praticaId);
  }

  function precompilaDaBozza(bozzaId: string, praticaId: string | null) {
    // Il pannello può essere stato chiuso, o riaperto su altro, mentre si legge.
    const ancoraQui = () => bozzaDalLink(window.location.search)?.bozzaId === bozzaId;
    leggiBozzaPerForm(bozzaId)
      .then((letta) => {
        if (!ancoraQui()) return;
        if (!letta) {
          setBozzaNelForm({ stato: "non_valida", bozzaId, praticaId });
          return;
        }
        const { bozza, sede, device } = letta;
        const modello = device ? modelloDaDevice(device) : null;
        reset({
          ...emptyFormValues(),
          insegna: sede?.nome ?? "",
          indirizzo: sede?.indirizzo ?? "",
          localita: sede?.citta ?? "",
          provincia: sede?.provincia ?? "",
          sede_id: bozza.dati.sede_id,
          cliente_id: bozza.dati.cliente_id,
          cliente_nome: sede?.cliente?.nome ?? "",
          sopralluoghi: bozza.dati.sopralluogo_id ? [bozza.dati.sopralluogo_id] : [],
          // Dalla matricola il backend ricava il device: è quella della cassaforte.
          dispositivo_matricola: device?.matricola ?? "",
          dispositivo_marca: modello?.marca ?? "",
          dispositivo_modello: modello?.modello ?? "",
          dispositivo_catalogo_id: modello?.catalogoId ?? "",
        });
        azzeraCensimento(device?.id ?? "");
        setBozzaNelForm({ stato: "valida", bozzaId, praticaId, letta });
      })
      .catch((err) => {
        if (ancoraQui()) setBozzaNelForm({ stato: "errore", bozzaId, praticaId, messaggio: getErrorMessage(err, "errore di rete.") });
      });
  }

  /**
   * Riporta a zero lo stato del censimento fra un'apertura e l'altra del
   * pannello. Vive fuori dal form, quindi `reset()` non lo tocca: senza questa
   * chiamata una spunta lasciata su un record si porterebbe dietro sul
   * successivo, censendo una matricola che nessuno ha guardato.
   */
  function azzeraCensimento(deviceId = "") {
    setDeviceSceltoId(deviceId);
    setCensisciDevice(false);
    setPossessoCensimento("");
  }

  function openEdit(record: InstallationSchedule) {
    setEditingRecord(record);
    setBozzaNelForm(null);
    // Suggerimento cliente (Punto B) solo per schede scollegate e solo per
    // admin/staff (l'endpoint è riservato a loro). Non scrive nulla: precompila
    // solo il campo su conferma manuale.
    setClienteSuggestion(null);
    if (canReassign && !record.cliente_id) {
      scheduleApi
        .suggerisciCliente(record.id)
        .then(({ suggestion }) => setClienteSuggestion(suggestion))
        .catch(() => setClienteSuggestion(null));
    }
    reset(recordToFormValues(record));
    azzeraCensimento(record.device_id ?? "");
  }

  // Pannello su `?open=<id>` (clic su una tessera, Calendario unificato,
  // scheda cliente, Report ordini) e su `?new=1` (pulsante, "+" di un giorno,
  // azione rapida della Dashboard, handoff da un Sopralluogo).
  const pannello = usePannelloRecord({
    records,
    isLoading,
    preparaNuovo: openCreate,
    preparaModifica: openEdit,
    chiaviCollegate: ["data", "sopralluogo", "cliente_id", "cliente_nome", "bozza", "pratica"],
  });
  const panelOpen = pannello.aperto;
  const apri = (r: InstallationSchedule) => pannello.apri(r.id);

  const onSubmit = handleSubmit(async (values) => {
    // cliente_nome è solo per il display del campo, non è una colonna: fuori dal payload.
    const { cliente_nome: _clienteNome, ...rest } = values;
    const segnaposto = isMesePlaceholder(values.ipotesi_data, values.stato);
    const payload = {
      // Senza i cinque campi OdA/OdV: si aggiungono qui sotto, e solo se cambiati.
      ...senzaRiferimentiOdoo(rest),
      assegnato_utente_id: values.assegnato_utente_id || null,
      ipotesi_data: values.ipotesi_data || null,
      // Su un segnaposto di mese l'orario non ha un giorno a cui riferirsi:
      // non si salva, come non si offre. Vale anche per una riga che aveva un
      // orario e torna segnaposto — restarci sarebbe un dato senza senso.
      orario_inizio: segnaposto ? null : values.orario_inizio || null,
      orario_fine: segnaposto ? null : values.orario_fine || null,
      data_effettiva: values.data_effettiva || null,
      installatore_id: values.installatore_id || null, // "" = nessun centro
      // FK opzionali: "" (select/handoff vuoto) → null (UUID valido o niente).
      cliente_id: values.cliente_id || null,
      sede_id: values.sede_id || null,
      dispositivo_catalogo_id: values.dispositivo_catalogo_id || null,
      // Sempre inviato, anche vuoto: il pannello e' l'unico posto da cui si
      // cambia, quindi qui "nessuno" vuol dire davvero scollegali tutti.
      sopralluoghi: values.sopralluoghi,
      // Le due chiavi del censimento NON sono colonne di installation_schedule:
      // la rotta le legge dal body grezzo e le usa per decidere se creare il
      // pezzo fisico. Il controllo del permesso `devices:create` e' li', questa
      // e' solo la richiesta.
      censisci_device: censisciDevice,
      censimento_tipo_possesso: possessoCensimento || null,
      // I cinque riferimenti OdA/OdV, nella forma del database e SOLO i
      // cambiati: fattura e garanzia sono di un ordine che può coprire altre
      // attività, e rimandare il valore letto all'apertura sovrascriverebbe
      // quello registrato nel frattempo da un'altra scheda (vedi
      // `payloadRiferimentiOdoo`). Per gli altri ruoli non partono affatto: lo
      // spread qui sopra li ha già tolti, e il backend li ignorerebbe comunque.
      ...(puoGestireOrdini ? payloadRiferimentiOdoo(values, riferimentiOdooDaRecord(editingRecord)) : {}),
    };

    try {
      const base = editingRecord ? "Installazione aggiornata." : "Installazione aggiunta al calendario.";
      // Il cast c'era gia' prima: nel form `tipo_dispositivo` e' una `string`
      // (il valore del `<select>`), non l'unione stretta del tipo del record.
      const daInviare = payload as scheduleApi.SchedulePayload;
      const { record, censimento } = editingRecord
        ? await scheduleApi.updateSchedule(editingRecord.id, daInviare)
        : await scheduleApi.createSchedule(daInviare);
      // Da una bozza (PR C2): l'installazione la conferma. Se nel frattempo la
      // bozza non è più valida, l'installazione resta salvata e lo si dice.
      const daBozza = !editingRecord && bozzaNelForm?.stato === "valida" ? bozzaNelForm : null;
      const avvisoBozza = daBozza ? await confermaDopoIlSalvataggio(daBozza.bozzaId, record.id) : null;
      if (daBozza) {
        showToast(
          avvisoBozza ? `${base} La bozza non risulta confermata: ${avvisoBozza}` : `${base} Bozza confermata.`,
          avvisoBozza ? "info" : "success"
        );
        if (daBozza.praticaId) {
          navigate(`/stato-lavori/${daBozza.praticaId}`);
          return;
        }
      }

      // Il censimento si dichiara sempre, e con l'esito vero. Un "salvato" che
      // tace su un device creato — o su un aggancio NON riuscito perche' il
      // pezzo aveva gia' la sua installazione — e' esattamente il silenzio da
      // cui e' nata questa segnalazione: il record sembrava a posto e il parco
      // non ne sapeva niente.
      if (daBozza) {
        // Già detto sopra, con l'esito della bozza.
      } else if (censimento?.occupata) {
        showToast(
          `${base} Dispositivo ${censimento.matricola} censito, ma non collegato a questa installazione: ` +
            "quel pezzo ne ha gia' una (un dispositivo puo' averne al massimo una).",
          "success"
        );
      } else if (censimento?.creato) {
        showToast(`${base} Dispositivo ${censimento.matricola} censito e collegato.`, "success");
      } else {
        showToast(base, "success");
      }
      pannello.chiudi();
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare l'installazione."), "error");
    }
  });

  async function handleDelete() {
    if (!editingRecord) return;
    const confirmed = window.confirm(`Eliminare "${editingRecord.insegna ?? "questo record"}" dal calendario?`);
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await scheduleApi.deleteSchedule(editingRecord.id);
      showToast("Record eliminato.", "success");
      pannello.chiudi();
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il record."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  // Dai permessi reali: `installazioni:create` include staff (il commento nella
  // mappa lo dice esplicitamente), l'elenco di ruoli scritto qui no.
  const canCreate = hasPermission("installazioni:create");

  async function handleSyncPull() {
    setIsSyncingPull(true);
    try {
      const { synced, lettura } = await scheduleApi.pullScheduleFromOdoo();
      const avviso = avvisoLettura(lettura);
      showToast(
        (avviso ? `${avviso.testo} · ` : "") + `${synced} eventi letti da Odoo Calendar.`,
        avviso ? "warning" : "success"
      );
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Lettura da Odoo Calendar non riuscita."), "error");
    } finally {
      setIsSyncingPull(false);
    }
  }

  async function handleSyncPush() {
    if (!editingRecord) return;
    setIsSyncingPush(true);
    try {
      const { record } = await scheduleApi.pushScheduleToOdoo(editingRecord.id);
      setEditingRecord(record);
      showToast("Inviato a Odoo Calendar.", "success");
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Invio a Odoo Calendar non riuscito."), "error");
    } finally {
      setIsSyncingPush(false);
    }
  }
  // `installazioni:delete` è [admin, staff]: qui c'era solo admin.
  const canDelete = hasPermission("installazioni:delete");

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Installazioni</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            label=""
            placeholder="Cerca insegna, località, indirizzo, matricola o tecnico…"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            className="min-w-[280px]"
          />

          {canCreate && (
            <Button variant="secondary" size="sm" loading={isSyncingPull} onClick={handleSyncPull}>
              Sincronizza da Odoo
            </Button>
          )}

          {canDelete && (
            <Button variant="secondary" size="sm" onClick={() => setBulkDeleteOpen(true)}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Elimina in blocco
            </Button>
          )}

          <ExportButton
            endpoint="/schedule/export"
            filename="calendario-installazioni.xlsx"
            fields={[
              { key: "stato", label: "Stato", type: "select", options: [...STATO_ORDER] },
              { key: "tipo_dispositivo", label: "Tipo dispositivo", type: "select", options: TIPI_DISPOSITIVO },
              { key: "zona", label: "Zona", type: "text" },
              { key: "data_da", label: "Data ipotizzata da", type: "date" },
              { key: "data_a", label: "Data ipotizzata a", type: "date" },
            ]}
          />

          {canCreate && (
            <Button size="sm" onClick={() => pannello.apriNuovo()}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuova installazione
            </Button>
          )}
        </div>
      </header>

      {attivazioneFilter && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-[#f97316]/30 bg-[#f97316]/5 px-4 py-2.5 text-sm">
          <span className="flex items-center gap-2 font-medium text-[#c2410c]">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            Stai vedendo solo le installazioni che aspettano l'Attivazione collegata.
          </span>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("attivazione_mancante");
              setSearchParams(next);
            }}
            className="shrink-0 font-semibold text-primary hover:underline"
          >
            Rimuovi filtro
          </button>
        </div>
      )}

      {ritardoFilter && (
        <RitardoBanner
          onRemove={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("ritardo");
            setSearchParams(next);
          }}
        />
      )}

      {activeFilter && (
        <section className="mb-6 rounded-card border border-accent/30 bg-accent/5 p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-bold text-ink">
              Filtrate per {activeFilter.label}:{" "}
              <span style={{ color: activeFilterKey === "stato" ? coloreStato(activeFilter.value) ?? "#6b7280" : "#18273d" }}>
                {activeFilter.value}
              </span>{" "}
              ({filteredRecords.length})
            </p>
            <button
              type="button"
              // Toglie i filtri arrivati da Dashboard e Statistiche, come prima;
              // ricerca e periodo del calendario sono della pagina e restano.
              onClick={() =>
                scrivi(
                  { ritardo: null, attivazione_mancante: null, stato: null, zona: null, tipo: null, installatore: null },
                  { push: true }
                )
              }
              className="text-xs font-semibold text-primary hover:underline"
            >
              Mostra tutte
            </button>
          </div>
          {filteredRecords.length === 0 ? (
            <p className="text-sm text-ink/50">Nessuna installazione per questo filtro.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {filteredRecords.map((r) => (
                <button
                  key={r.id}
                  onClick={() => apri(r)}
                  className="flex items-center gap-3 rounded-lg border border-line bg-white p-3 text-left text-sm hover:bg-primary/5"
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: coloreStato(r.stato) ?? "#6b7280" }}
                  />
                  <span className="flex-1">
                    <span className="font-semibold text-ink">{r.insegna ?? "Senza insegna"}</span>
                    <span className="text-ink/50"> · {r.localita ?? "—"}</span>
                  </span>
                  <span className="text-xs text-ink/40">
                    {etichettaIpotesiData(r.ipotesi_data, r.stato) ?? "Senza data"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* --- Da pianificare + fascia calendario ------------------------------
          Affiancati (e la pila in verticale) perché il trascinamento richiede
          di vedere insieme l'origine e la destinazione: con la vecchia fila
          orizzontale sopra al calendario, tessera e giorno di destinazione non
          stavano quasi mai nella stessa schermata. Sotto xl si impilano. */}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        accessibility={{ announcements: annunciTrascinamento, screenReaderInstructions: istruzioniTrascinamento }}
      >
        {/* Per un operatore una pagina senza nessuna tessera non vuol dire "non
            c'è lavoro": vuol dire che il suo centro ha gia distribuito tutto.
            Senza dirlo, un calendario vuoto si legge come un guasto. Sta a
            livello di pagina e non dentro le due colonne perché è la pagina
            intera a essere vuota. */}
        {!isLoading && records.length === 0 && isOperatore && !ritardoFilter && !attivazioneFilter && !activeFilter && (
          <div className="mb-6 rounded-card border border-line bg-white py-6 shadow-card">
            <ListaVuotaOperatore organizzazione={organizzazione} />
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[300px_1fr] xl:items-start">
          <DaPianificare records={daPianificare} onApri={apri} isLoading={isLoading} />

          <div className="min-w-0">
            <CalendarBand
              items={records}
              isLoading={isLoading}
              droppable
              getId={(r) => r.id}
              // I segnaposto di mese non hanno un giorno in cui stare: `null`
              // li tiene fuori dalla fascia (è il contratto dichiarato da
              // getDate) e vivono nella pila qui accanto, vedi `daPianificare`.
              // Una volta "Programmata" (o oltre) il giorno 1 è vero e basta.
              getDate={(r) => (isMesePlaceholder(r.ipotesi_data, r.stato) ? null : r.ipotesi_data)}
              getColor={(r) => coloreStato(r.stato) ?? "#6b7280"}
              getTitle={(r) => r.insegna ?? "Senza insegna"}
              getSubtitle={(r) => r.localita}
              // Qui le due estremità arrivano grezze, non composte come sulle
              // schede del Calendario unificato: si compongono con la stessa
              // regola, perché la stessa installazione non si legga in due modi
              // diversi a seconda della pagina da cui la si guarda.
              getOrario={(r) => intervalloOrario(r.orario_inizio, r.orario_fine)}
              renderBadges={(r) => (
                <>
                  <Badge stato={r.stato}>{r.stato}</Badge>
                  {r.in_ritardo && <Badge color="danger">In ritardo</Badge>}
                  {r.attivazione_mancante && <Badge color="warning">Attivazione da creare</Badge>}
                </>
              )}
              onItemClick={apri}
              onAddDay={canCreate ? (iso) => pannello.apriNuovo({ data: iso }) : undefined}
              vistaIniziale={vistaIniziale}
              dataIniziale={dataIniziale}
              onRangeChange={cambioPeriodo}
            />
          </div>
        </div>

        {/* Anteprima che segue il puntatore: senza, la tessera resterebbe ferma
            nella pila e il gesto non avrebbe riscontro visivo. */}
        <DragOverlay dropAnimation={null}>
          {recordTrascinato && (
            <div className="w-[280px] rotate-1 cursor-grabbing rounded-card border border-primary bg-white p-3 shadow-lift">
              <p className="truncate text-sm font-semibold text-ink">{recordTrascinato.insegna ?? "Senza insegna"}</p>
              <p className="truncate text-xs text-ink/50">{recordTrascinato.localita ?? "—"}</p>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <BulkDeleteModal open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} onDeleted={() => loadRecords()} />

      {/* --- Pannello slide-in di creazione/modifica --------------------------- */}
      <SlideOver
        open={panelOpen}
        onClose={pannello.chiudi}
        title={editingRecord ? "Modifica installazione" : "Nuova installazione"}
        footer={
          <>
            {editingRecord && canDelete && (
              <Button type="button" variant="ghost" loading={isDeleting} onClick={handleDelete} className="mr-auto text-accent">
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Elimina
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={pannello.chiudi}>
              Annulla
            </Button>
            <Button type="submit" form="schedule-form" loading={isSubmitting} disabled={bozzaInCaricamento}>
              Salva
            </Button>
          </>
        }
      >
        {/* Il promemoria vive qui e non solo come badge in lista: è aprendo la
            scheda che si decide cosa fare, ed è qui che serve la scorciatoia.
            Compare solo a record esistente — un'installazione non ancora
            salvata non ha un id a cui agganciare l'attivazione. */}
        {editingRecord?.attivazione_mancante && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#f97316]/30 bg-[#f97316]/5 px-4 py-2.5 text-sm">
            <span className="flex items-center gap-2 font-medium text-[#c2410c]">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              Il servizio Pre Credit è da attivare: manca l'Attivazione collegata.
            </span>
            <Link
              to={`/activations?new=1&schedule=${editingRecord.id}`}
              className="shrink-0 font-semibold text-primary hover:underline"
            >
              Creala adesso →
            </Link>
          </div>
        )}

        <form id="schedule-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {!editingRecord && bozzaNelForm && <AvvisoBozza stato={bozzaNelForm} />}
          <fieldset disabled={bozzaInCaricamento} className="contents">
            <SedeAutocomplete
              label="Insegna"
              value={watch("insegna")}
              onChange={(v) => setValue("insegna", v)}
              sedeId={watch("sede_id")}
              onSedeIdChange={(v) => setValue("sede_id", v)}
              onSelect={(s) => {
                // Scegliendo una sede già in anagrafica, indirizzo/città/
                // provincia seguono automaticamente — evita di ridigitarli e
                // ottenere una variante leggermente diversa della stessa sede
                // (es. "Torino" vs "TORINO"), che altrimenti sembrerebbe un
                // punto vendita nuovo invece dello stesso già censito. Precompila
                // anche l'indirizzo (prima non usato da questo form), utile
                // soprattutto per la precisione del geocoding automatico.
                // Insegna e sede_id li imposta già il componente.
                if (s.indirizzo) setValue("indirizzo", s.indirizzo);
                if (s.citta) setValue("localita", s.citta);
                if (s.provincia) setValue("provincia", s.provincia);
              }}
            />
            <Input
              label="Indirizzo"
              placeholder="Via e civico"
              hint="Insieme a Località, usato per calcolare le coordinate sulla Mappa."
              {...register("indirizzo")}
            />
            <Input label="Località" {...register("localita")} />
            <Input label="Provincia" {...register("provincia")} maxLength={2} className="uppercase" />

            {/* Cliente (anagrafica) — Punto B. Solo admin/staff: collega la scheda
                a un cliente reale. Suggerimento facoltativo su match sede, sempre
                a conferma manuale (non scrive nulla finché non si salva). */}
            {canReassign && (
              <div className="flex flex-col gap-1.5">
                <ClienteAutocomplete
                  label="Cliente"
                  value={watch("cliente_nome")}
                  onChange={(v) => {
                    setValue("cliente_nome", v);
                    if (v === "") setValue("cliente_id", "");
                  }}
                  onSelect={(c) => {
                    setValue("cliente_id", c.id);
                    setValue("cliente_nome", c.nome);
                  }}
                />
                {clienteSuggestion && !watch("cliente_id") && (
                  <button
                    type="button"
                    onClick={() => {
                      setValue("cliente_id", clienteSuggestion.cliente_id);
                      setValue("cliente_nome", clienteSuggestion.cliente_nome);
                      setValue("sede_id", clienteSuggestion.sede_id);
                    }}
                    className="self-start rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-left text-xs text-primary hover:bg-primary/10"
                  >
                    💡 Suggerito: <span className="font-semibold">{clienteSuggestion.cliente_nome}</span>{" "}
                    <span className="text-ink/50">(match sede “{clienteSuggestion.sede_nome}”)</span> — Usa
                  </button>
                )}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Zona</label>
              <select className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink" {...register("zona")}>
                <option value="">—</option>
                {ZONE.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Tipo dispositivo</label>
              <select
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                {...register("tipo_dispositivo")}
              >
                <option value="">—</option>
                {TIPI_DISPOSITIVO.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* UN campo solo, collegato al Catalogo — non tre liberi.
                Prima c'erano «Macchina» (testo scollegato da tutto), «Marca» e
                «Modello»: tre posti per dire la stessa cosa, che nessuno teneva
                d'accordo. La forma di riferimento e' quella della scheda
                Dispositivo, in produzione da mesi: un campo, testo libero, ma che
                quando si sceglie dai suggerimenti collega il modello vero.

                `dispositivo_marca` e `dispositivo_modello` restano nel database e
                si riempiono da qui: non sono doppioni da cancellare — li legge
                l'export, e `dispositivo_modello` diventa il `modello_raw` del
                device quando si spunta il censimento. Sono ancora due colonne, ma
                una sola casella da compilare. */}
            <DispositivoAutocomplete
              label="Dispositivo"
              placeholder="Digita marca, modello o sigla…"
              tipoDispositivo={watch("tipo_dispositivo") as TipoDispositivo | ""}
              value={watch("dispositivo_modello")}
              onChange={(v) => {
                setValue("dispositivo_modello", v);
                // Testo scritto a mano: il collegamento al catalogo non vale piu',
                // e la marca nemmeno — restava quella del modello di prima. Stessa
                // regola di `modelloDaDevice` sul percorso della matricola.
                setValue("dispositivo_catalogo_id", "");
                setValue("dispositivo_marca", "");
              }}
              onSelect={(d) => {
                setValue("dispositivo_marca", d.marca);
                setValue("dispositivo_modello", `${d.marca} ${d.modello}`);
                setValue("dispositivo_catalogo_id", d.id);
              }}
            />
            {watch("dispositivo_catalogo_id") && (
              <DispositivoMateriale dispositivoId={watch("dispositivo_catalogo_id")} />
            )}
            {/* Il selettore del pezzo FISICO, lo stesso gia' usato da Attivazioni e
                Interventi. Prima qui c'era un `Input` di testo libero, e la
                differenza non era estetica: il testo non veniva mai tradotto nel
                device che nomina, quindi l'installazione restava orfana e il
                parco non ne sapeva niente. */}
            <DeviceFisicoAutocomplete
              label="Matricola dispositivo"
              value={watch("dispositivo_matricola")}
              onChange={(v) => setValue("dispositivo_matricola", v)}
              deviceId={deviceSceltoId}
              onDeviceIdChange={setDeviceSceltoId}
              onSelect={(d) => {
                // Marca e modello descrivono lo STESSO oggetto della matricola
                // appena scelta: lasciarli da riscrivere a mano invitava a
                // scriverli diversi da come sono censiti, e da lì nascono le
                // varianti dello stesso modello. Solo alla scelta esplicita dalla
                // tendina, mai digitando. La regola sta in `modelloDaDevice`
                // perché la applica anche Attivazioni.
                const { marca, modello, catalogoId } = modelloDaDevice(d);
                // `null` vuol dire «non ricavabile»: il campo si lascia com'è,
                // svuotarlo sarebbe una perdita e non una precompilazione. `""`
                // invece è una richiesta esplicita di svuotare, e va eseguita.
                if (marca !== null) setValue("dispositivo_marca", marca);
                if (modello !== null) setValue("dispositivo_modello", modello);
                // La chiave si riscrive sempre, vuoto compreso: vedi il commento
                // su `catalogoId` in modelloDaDevice.
                setValue("dispositivo_catalogo_id", catalogoId ?? "");
              }}
              sedeId={watch("sede_id") || null}
              hint={
                watch("sede_id")
                  ? "I pezzi che stanno in questa sede compaiono per primi."
                  : "Scegli prima la sede per vedere subito la cassaforte che sta li'."
              }
            />
            <CensimentoDevice
              matricola={watch("dispositivo_matricola")}
              deviceId={deviceSceltoId}
              censisci={censisciDevice}
              onCensisciChange={setCensisciDevice}
              tipoPossesso={possessoCensimento}
              onTipoPossessoChange={setPossessoCensimento}
            />
            <Input label="Tecnico" {...register("tecnico")} />

            {/* Il collegamento ai sopralluoghi da cui l'installazione deriva.
                Vale in creazione E in modifica: prima l'unico modo di collegarlo
                era l'handoff dal pannello del Sopralluogo, quindi un'installazione
                gia' salvata non si poteva piu' agganciare a niente. */}
            <SopralluoghiCollegati
              collegati={
                editingRecord
                  ? (editingRecord.sopralluoghi ?? []).map((r) => r.sopralluogo)
                  : // Da una bozza: il sopralluogo d'origine, già collegato (PR C2).
                    bozzaNelForm?.stato === "valida" && bozzaNelForm.letta.sopralluogo
                    ? [bozzaNelForm.letta.sopralluogo]
                    : []
              }
              value={watch("sopralluoghi")}
              onChange={(ids) => setValue("sopralluoghi", ids)}
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Centro Assistenza</label>
              {canReassign ? (
                <select
                  className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                  {...register("installatore_id")}
                >
                  <option value="">— Nessuno —</option>
                  {centri.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              ) : (
                // Un Centro Assistenza vede l'assegnazione ma non può cambiarla.
                <p className="rounded-lg border border-line bg-ink/[0.02] px-3 py-2 text-sm text-ink">
                  {editingRecord?.installatore ?? "—"}
                </p>
              )}
            </div>

            <AssegnatarioSelect
              value={watch("assegnato_utente_id")}
              onChange={(id) => setValue("assegnato_utente_id", id)}
              cliente_id={watch("cliente_id")}
              centro_assistenza_id={watch("installatore_id") || centroDelChiamante}
              aiuto="La persona incaricata, dentro il centro assegnato sopra."
            />

            <div className="grid grid-cols-2 gap-3">
              <Input label="Data ipotizzata" type="date" {...register("ipotesi_data")} />
              <Input label="Data effettiva" type="date" {...register("data_effettiva")} />
            </div>

            {/* Il giorno 1 di una riga non ancora programmata è un SEGNAPOSTO DI
                MESE, non una data (vedi utils/dataIpotesi.ts): un orario su
                «Settembre 2026» non vuol dire niente. Il database non lo impedisce
                — sarebbe un CHECK condizionale, e qui non ce ne sono — quindi la
                regola vive qui. Spente e spiegate, non nascoste: un campo che
                sparisce mentre si scrive la data sembra un guasto. */}
            <CampiOrario
              inizio={register("orario_inizio")}
              fine={register("orario_fine")}
              disabilitato={segnapostoMese}
              nota={
                segnapostoMese
                  ? "Questa riga indica un mese, non un giorno: scegli un giorno preciso (o portala a «Programmata») per darle un orario."
                  : undefined
              }
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Stato</label>
              <select className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink" {...register("stato")}>
                {STATO_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Promemoria, non automatismo: l'Attivazione porta dati che qui non
                si conoscono ancora (vigilanza, contratto, LAN/SIM), e crearla da
                sola li riempirebbe di nulla. Qui si dichiara solo che va fatta. */}
            <label className="flex items-start gap-2.5 rounded-lg border border-line bg-ink/[0.02] px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                {...register("richiede_attivazione")}
              />
              <span className="text-sm text-ink">
                Da attivare il servizio Pre Credit
                <span className="mt-0.5 block text-xs text-ink/50">
                  L'app lo ricorda finché non crei l'Attivazione collegata.
                </span>
              </span>
            </label>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Note</label>
              <textarea
                rows={3}
                className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
                {...register("note")}
              />
            </div>

            {/* Riferimenti OdA/OdV — solo admin/staff: su questo modulo lavorano
                anche i Centri Assistenza, e il numero d'ordine al fornitore non è
                cosa loro. `distinguiDaInGaranzia` perché la colonna `in_garanzia`
                dell'installazione esiste già e chiede una cosa diversa. */}
            {puoGestireOrdini && (
              <RiferimentiOdoo
                idPrefisso="installazione"
                ordini={editingRecord?.ordini_odoo}
                distinguiDaInGaranzia
                valori={{
                  numero_oda: watch("numero_oda"),
                  oda_fattura_ricevuta: watch("oda_fattura_ricevuta"),
                  numero_odv: watch("numero_odv"),
                  odv_in_garanzia: watch("odv_in_garanzia"),
                  odv_fatturato: watch("odv_fatturato"),
                  spunte_toccate: watch("spunte_toccate"),
                }}
                onChange={(v) => {
                  setValue("numero_oda", v.numero_oda);
                  setValue("oda_fattura_ricevuta", v.oda_fattura_ricevuta);
                  setValue("numero_odv", v.numero_odv);
                  setValue("odv_in_garanzia", v.odv_in_garanzia);
                  setValue("odv_fatturato", v.odv_fatturato);
                  setValue("spunte_toccate", v.spunte_toccate);
                }}
              />
            )}
          </fieldset>
        </form>

        {editingRecord && canCreate && (
          <div className="mt-6 border-t border-line pt-4">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Odoo Calendar</p>
            <div className="flex items-center justify-between">
              <p className="text-xs text-ink/50">
                {editingRecord.odoo_event_id
                  ? `Collegato all'evento Odoo #${editingRecord.odoo_event_id}${
                      editingRecord.odoo_last_sync
                        ? ` · ultimo invio ${new Date(editingRecord.odoo_last_sync).toLocaleString("it-IT")}`
                        : ""
                    }`
                  : "Non ancora collegato a nessun evento Odoo."}
              </p>
              <Button type="button" size="sm" variant="secondary" loading={isSyncingPush} onClick={handleSyncPush}>
                {editingRecord.odoo_event_id ? "Aggiorna su Odoo" : "Invia a Odoo"}
              </Button>
            </div>
          </div>
        )}

        {editingRecord && (
          <div className="mt-6 border-t border-line pt-4">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Foto e video</p>
            <MediaUploader entityType="schedule" entityId={editingRecord.id} />
          </div>
        )}
      </SlideOver>
    </div>
  );
}

/**
 * Eliminazione in blocco per filtro — mai a scatola chiusa: prima mostra
 * quante righe corrisponderebbero (senza cancellare nulla), solo dopo
 * conferma esplicita esegue l'eliminazione vera. Stesso pattern già usato
 * in Interventi, per lo stesso motivo: troppe righe "Da programmare" da
 * cancellare una alla volta dal calendario.
 */
function BulkDeleteModal({ open, onClose, onDeleted }: { open: boolean; onClose: () => void; onDeleted: () => void }) {
  const { showToast } = useToast();
  const [stato, setStato] = useState("");
  const [zona, setZona] = useState("");
  const [tipoDispositivo, setTipoDispositivo] = useState<"" | TipoDispositivo>("");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const [preview, setPreview] = useState<number | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  function reset() {
    setStato("");
    setZona("");
    setTipoDispositivo("");
    setDataDa("");
    setDataA("");
    setPreview(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const filters = {
    stato: stato || undefined,
    zona: zona || undefined,
    tipo_dispositivo: tipoDispositivo || undefined,
    data_da: dataDa || undefined,
    data_a: dataA || undefined,
  };
  const hasAnyFilter = Boolean(stato || zona || tipoDispositivo || dataDa || dataA);

  async function handleCheck() {
    setIsChecking(true);
    setPreview(null);
    try {
      const { count } = await scheduleApi.bulkDeletePreview(filters);
      setPreview(count);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile calcolare l'anteprima."), "error");
    } finally {
      setIsChecking(false);
    }
  }

  async function handleConfirmDelete() {
    if (preview === null) return;
    const confirmed = window.confirm(
      `Stai per eliminare DEFINITIVAMENTE ${preview} installazioni dal calendario. L'operazione non è reversibile. Continuare?`
    );
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      const { deleted } = await scheduleApi.bulkDeleteSchedule(filters);
      showToast(`${deleted} installazioni eliminate.`, "success");
      handleClose();
      onDeleted();
    } catch (err) {
      showToast(getErrorMessage(err, "Eliminazione non riuscita."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Modal open={open} onClose={handleClose} title="Elimina installazioni in blocco" size="sm">
      <div className="flex flex-col gap-4">
        <p className="text-xs text-ink/50">
          Imposta almeno un filtro, poi controlla quante installazioni corrisponderebbero prima di eliminarle —
          nessuna eliminazione avviene finché non confermi esplicitamente il numero mostrato.
        </p>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Stato</label>
          <select
            value={stato}
            onChange={(e) => {
              setStato(e.target.value);
              setPreview(null);
            }}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            <option value="">Tutti</option>
            {STATO_ORDER.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Zona</label>
          <select
            value={zona}
            onChange={(e) => {
              setZona(e.target.value);
              setPreview(null);
            }}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            <option value="">Tutte</option>
            {ZONE.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Tipo dispositivo</label>
          <select
            value={tipoDispositivo}
            onChange={(e) => {
              setTipoDispositivo(e.target.value as typeof tipoDispositivo);
              setPreview(null);
            }}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            <option value="">Tutti</option>
            {TIPI_DISPOSITIVO.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">Data ipotizzata da</label>
            <input
              type="date"
              value={dataDa}
              onChange={(e) => {
                setDataDa(e.target.value);
                setPreview(null);
              }}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">Data ipotizzata a</label>
            <input
              type="date"
              value={dataA}
              onChange={(e) => {
                setDataA(e.target.value);
                setPreview(null);
              }}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            />
          </div>
        </div>

        {preview !== null && (
          <div className="rounded-lg bg-accent/10 px-3 py-2 text-sm text-ink">
            <strong>{preview}</strong> {preview === 1 ? "installazione corrisponde" : "installazioni corrispondono"} a
            questi filtri.
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="ghost" onClick={handleClose}>
            Annulla
          </Button>
          {preview === null ? (
            <Button type="button" loading={isChecking} disabled={!hasAnyFilter} onClick={handleCheck}>
              Controlla quanti sono
            </Button>
          ) : (
            <Button type="button" loading={isDeleting} disabled={preview === 0} onClick={handleConfirmDelete}>
              Elimina {preview} installazioni
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
